import React from 'react';
import TabContext from '@mui/lab/TabContext';

import { useDispatch, useSelector } from 'react-redux';
import { selectCommandByNodeId, State, tabActions } from '@store/index';
import { TabList, TabPanel } from '@mui/lab';
import { Alert, AlertTitle, Stack, Tab } from '@mui/material';
import { NodeField, NodeFieldType } from '../../';

import { ArticleOutlined } from '@mui/icons-material';
import { useModules } from '@common/hooks/useModules';
import { FlowModule } from '@store/async';
import { Button } from '@common/Button';
import { NodeInputExpression, NodeSidebar, NodeSidebarTab, NodeSidebarTabsContainer } from '@common/Node/NodeSidebar';
import { NodeData } from '@common/Node/NodeSidebar/NodeData';

interface Props {
  tab: string;
  setTab: React.Dispatch<string>;
}

export const ModNodeTabs: React.FC<Props> = ({ tab, setTab }) => {
  const dispatch = useDispatch();
  const selectedNode = useSelector((state: State) => state.tabs.active.selectedNode);
  const trace = useSelector((state: State) => state.tabs.active.run);

  const { modules, error } = useModules();

  const command = useSelector((state: State) => selectCommandByNodeId(state, state.tabs.active.selectedNode));
  if (!command) return null;

  const moduleId = command.state?.moduleId;
  const mod = modules.find((mod: FlowModule) => mod.flow_id === moduleId);

  const fields = command.fields;
  const inputFields = fields.filter((field) => field.type === NodeFieldType.InputExpression);

  const nodeErrors = trace.nodeErrors;
  const nodeError = nodeErrors.find((error) => error.node_id === selectedNode);

  return (
    <NodeSidebar>
      {nodeError && (
        <Stack padding='16px 20px 0 20px'>
          <Alert
            severity='error'
            action={
              <Button
                customColor='error'
                small
                onClick={() => dispatch(tabActions.clearWorkflowNodeError(command.node.id))}
              >
                Clear
              </Button>
            }
          >
            {nodeError.error}
          </Alert>
        </Stack>
      )}
      <TabContext value={tab}>
        <NodeSidebarTabsContainer color={command.config.background}>
          <TabList onChange={(_, newTab) => setTab(newTab)}>
            <Tab disableRipple label='Node' value='1' />
            <Tab title='Data' disableRipple iconPosition='start' icon={<ArticleOutlined />} value='2' />
          </TabList>
        </NodeSidebarTabsContainer>
        <TabPanel value='1'>
          <NodeSidebarTab>
            {error && (
              <Alert severity='error'>
                <AlertTitle>Failed to Load Modules</AlertTitle>
                {`A fatal error has occured. Please refresh the page and try again. If this issue persists, please contact support.\n\nError message: `}
                {typeof error === 'string' ? error : JSON.stringify(error)}
              </Alert>
            )}
            {mod?.description && <Alert severity='info'>{mod?.description}</Alert>}
            {inputFields.map((field: NodeField) => (
              <NodeInputExpression key={field.id} field={field} command={command} />
            ))}
          </NodeSidebarTab>
        </TabPanel>
        <TabPanel value='2'>
          <NodeData command={command} />
        </TabPanel>
      </TabContext>
    </NodeSidebar>
  );
};
